/**
 * Runtime Proof Guard
 * Authority: RUNTIME_DERIVATION_LAW.md
 * Purpose: Validate runtime derivation from build + deploy + backend proofs
 */

import React from 'react';
import BUILD_ID from '../version.json';
import { getBackendUrl } from "../config/urlAuthority";
import { getSystemIdentity, getBuildVersion, rawFetch } from "@/lib/api";

const RUNTIME_PROOF_MODE = process.env.REACT_APP_RUNTIME_PROOF_MODE || 'relaxed';

export class RuntimeDerivationError extends Error {
    constructor(failureClass, evidence) {
        super(`${failureClass}: ${evidence}`);
        this.failureClass = failureClass;
        this.evidence = evidence;
    }
}

/**
 * Safe JSON Fetch
 * Validates HTTP status and Content-Type before parsing JSON.
 * Prevents parse errors when backend returns HTML or non-JSON responses.
 */
async function safeJsonFetch(url, expectedClass = 'IDENTITY_CONTRACT_BROKEN') {
    const response = await rawFetch(url);

    // Check HTTP status
    if (!response.ok) {
        throw new RuntimeDerivationError(
            expectedClass,
            `${url} returned ${response.status} ${response.statusText}`
        );
    }

    // Check Content-Type
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        const body = await response.text();
        throw new RuntimeDerivationError(
            expectedClass,
            `${url} returned non-JSON (Content-Type: ${contentType || 'missing'}). Body: ${body.substring(0, 100)}`
        );
    }

    return response.json();
}

/**
 * Validate Runtime Derivation
 * 
 * Fetches backend proofs and compares with build artifacts.
 * Fails with taxonomy class if mismatch detected.
 */
export async function validateRuntimeDerivation() {
    console.log('[Runtime Guard] Validating runtime derivation...');

    try {
        // 1. Fetch build artifacts (local, should always work)
        const buildArtifacts = await getBuildVersion();
        console.log('[Runtime Guard] Build artifacts:', buildArtifacts);

        // 2. Fetch backend proofs (using safe fetch)
        const backendUrl = getBackendUrl();
        const backendIdentity = await safeJsonFetch(
            `${backendUrl}/api/system/identity`,
            'IDENTITY_CONTRACT_BROKEN'
        );
        const dataContract = await safeJsonFetch(
            `${backendUrl}/api/system/data-contract`,
            'IDENTITY_CONTRACT_BROKEN'
        );
        console.log('[Runtime Guard] Backend identity:', backendIdentity);
        console.log('[Runtime Guard] Data contract:', dataContract);

        // 3. Validate derivation
        const violations = [];

        // Check: Build commit matches backend commit
        // Contract: Short hash (7 chars) and full hash (40 chars) are semantically equivalent
        // if the full hash starts with the short hash
        if (buildArtifacts.hash && backendIdentity.commit !== 'unknown') {
            const shortHash = buildArtifacts.hash;
            const fullHash = backendIdentity.commit;

            // Normalize: check if either is a prefix of the other
            const isMatch = fullHash.startsWith(shortHash) || shortHash.startsWith(fullHash);

            if (!isMatch) {
                violations.push({
                    class: 'RUNTIME_DERIVATION_BROKEN',
                    evidence: `Build commit (${buildArtifacts.shortHash}) ≠ Backend commit (${backendIdentity.commit})`
                });
            }
        }

        // Check: Data contract shows non-empty counts
        if (dataContract.counts) {
            if (dataContract.counts.founders < 1) {
                violations.push({
                    class: 'DATA_PRESENTATION_WITHOUT_PROOF',
                    evidence: `Founders count: ${dataContract.counts.founders} (expected >= 1)`
                });
            }
        }

        // 4. Handle violations
        if (violations.length > 0) {
            console.error('[Runtime Guard] Violations detected:', violations);

            if (RUNTIME_PROOF_MODE === 'strict') {
                // Fail hard: throw error to block render
                throw new RuntimeDerivationError(
                    violations[0].class,
                    violations[0].evidence
                );
            } else {
                // Relaxed: log warning, allow render
                console.warn('[Runtime Guard] RELAXED MODE: Violations logged but not blocking');
                violations.forEach(v => console.warn(`  - ${v.class}: ${v.evidence}`));
            }
        } else {
            console.log('[Runtime Guard] ✅ Runtime derivation validated');
        }

        return {
            valid: violations.length === 0,
            violations,
            mode: RUNTIME_PROOF_MODE
        };

    } catch (error) {
        console.error('[Runtime Guard] Validation failed:', error);

        if (RUNTIME_PROOF_MODE === 'strict') {
            throw new RuntimeDerivationError(
                'UI_CONTRACT_NOT_CONSUMED',
                `Failed to validate runtime: ${error.message}`
            );
        }

        return {
            valid: false,
            violations: [{
                class: 'UI_CONTRACT_NOT_CONSUMED',
                evidence: error.message
            }],
            mode: RUNTIME_PROOF_MODE
        };
    }
}

/**
 * Runtime Proof Guard Component
 * 
 * Wraps app to enforce runtime derivation validation.
 * Shows error screen if validation fails in strict mode.
 */
export function RuntimeProofGuard({ children }) {
    const [validationResult, setValidationResult] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        validateRuntimeDerivation()
            .then(result => {
                setValidationResult(result);
                setLoading(false);
            })
            .catch(error => {
                setValidationResult({
                    valid: false,
                    violations: [{
                        class: error.failureClass || 'UNKNOWN_ERROR',
                        evidence: error.message
                    }],
                    mode: RUNTIME_PROOF_MODE
                });
                setLoading(false);
            });
    }, []);

    // ERROR STATE: Strict Violation
    if (validationResult && !validationResult.valid && RUNTIME_PROOF_MODE === 'strict') {
        const violation = validationResult.violations[0];
        return (
            <div className="p-8 flex items-center justify-center h-full text-center">
                <div className="max-w-2xl bg-white p-8 rounded-lg border border-red-200 shadow-sm">
                    <h3 className="text-xl font-semibold text-red-700 mb-4">System Integrity Violation</h3>
                    <div className="text-left bg-slate-50 p-4 rounded border border-slate-200 mb-6 font-mono text-sm">
                        <p><strong>Code:</strong> {violation.class}</p>
                        <p><strong>Evidence:</strong> {violation.evidence}</p>
                    </div>
                    <p className="text-slate-600 mb-6">The application runtime does not match the certified build signature. Execution halted by RuntimeProofGuard.</p>
                </div>
            </div>
        );
    }

    // Default: Render Children
    return (
        <>
            {children}
        </>
    );
}
